import { Button } from "@ameelio/ui";
import { useMutation, useQuery } from "@apollo/client/react";
import { Alert, AlertTitle, Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Connection,
  ConnectionStatus,
  CorrespondentFeature,
  Facility,
  SystemUserStatus,
} from "../api/graphql";
import { appendItem } from "../client";
import { CreatePendingConnectionDocument } from "../ConnectionsFlow/CreatePendingConnection.generated";
import { unaccompaniedVisitAllowed } from "../Events/utils";
import { ContentSkeleton } from "../lib/closet";
import connectionFeatureToFacilityFeature from "../lib/connectionFeatureToFacilityFeature";
import ContactRestrictionsAlert from "../lib/ContactRestrictionsAlert";
import featureEnabled from "../lib/featureEnabled";
import useApolloErrorHandler from "../lib/handleApolloError";
import Link from "../lib/Link";
import permittedFeaturesList from "../lib/permittedFeaturesList";
import { PersonalRelationship } from "../lib/Relationship";
import Screen from "../lib/Screen";
import useFeaturePermitted from "../lib/useFeaturePermitted";
import { RejectedIDAlert } from "../lib/useIdRejectionStatus";
import { useCurrentCorrespondent } from "../SessionBoundary";
import ArchiveConnectionButton from "./ArchiveConnectionButton";
import ConnectionStatusChip from "./ConnectionStatusChip";
import ContactRejectedAlert from "./ContactRejectedAlert";
import ContactDetails from "./ContactsDetails";
import EditConnectionRelationshipButton from "./EditConnectionRelationshipButton";
import { GetContactDetailsDocument } from "./GetContactDetails.generated";

export default function ContactDetailsScreen() {
  const { t } = useTranslation();
  const handleApolloError = useApolloErrorHandler();
  const navigate = useNavigate();
  const newResubmitUx = featureEnabled("CONTACT_RESUBMIT_NEW_UX");
  const user = useCurrentCorrespondent();

  const { connectionId } = useParams<{
    connectionId: string;
  }>();
  if (!connectionId) throw new Error("missing connectionId param");

  const mayScheduleEvents =
    useFeaturePermitted(CorrespondentFeature.ScheduleMeetings) &&
    unaccompaniedVisitAllowed(user, connectionId);

  const { data, error } = useQuery(GetContactDetailsDocument, {
    fetchPolicy: "cache-and-network",
    variables: { connectionId },
  });

  // By the time this is called, data will exist - have
  // to add here because the hook can't be called conditionally
  // after the early returns below this
  const [createPendingConnection, { loading: createPendingLoading }] =
    useMutation(CreatePendingConnectionDocument, {
      variables: {
        input: {
          inmateId: data?.connection.inmate.id || "",
          relationship:
            data?.connection.relationship || PersonalRelationship.Other,
        },
      },
      onError: (e) => handleApolloError(e),
      update: (cache, { data: createPendingData }) => {
        if (!createPendingData) return;
        cache.modify({
          id: cache.identify({ __typename: "Visitor", id: user.id }),
          fields: {
            connections: appendItem(
              createPendingData.createPendingConnection.connection
            ),
          },
        });
      },
    });

  if (error) throw error;
  if (!data) return <ContentSkeleton />;

  const { connection } = data;
  const { facility } = connection.inmate;
  // organization-backed connections get their relationship from the
  // organization type, so it can't be chosen
  const canEditRelationship =
    user.__typename === "Visitor" && !connection.organizationMembership;

  const permittedFeatures = permittedFeaturesList({
    features: connection.features.map((f) =>
      connectionFeatureToFacilityFeature(f)
    ),
    activeRestrictions: connection.activeRestrictions.map((r) => r.service),
  });

  const action =
    mayScheduleEvents && connection.status === ConnectionStatus.Active ? (
      <Link
        button
        variant="outlined"
        to="/events/new"
        state={{
          connectionId: connection.id,
          fromPath: `/contacts/${connection.id}`,
          fromName: t("Contacts"),
        }}
        sx={{ flexGrow: 1 }}
      >
        {t("Schedule event")}
      </Link>
    ) : null;

  const mayRequestConnections = user.correspondentFeatures.includes(
    CorrespondentFeature.RequestConnections
  );

  const visitorHasRejectedID =
    user.__typename === "Visitor" &&
    user.systemRelationships.find(
      (sr) =>
        sr.status === SystemUserStatus.Rejected &&
        facility.system.id === sr.system.id
    );

  return (
    <Screen
      showBack
      backFallback={newResubmitUx ? "/contacts" : undefined}
      showNotifications
      headerAction={action}
      title={connection.contact.fullName}
    >
      {!newResubmitUx && visitorHasRejectedID && (
        <Box sx={{ my: 4 }}>
          <RejectedIDAlert />
        </Box>
      )}
      {newResubmitUx &&
        connection.status === ConnectionStatus.Rejected &&
        mayRequestConnections && (
          <Alert severity="error" sx={{ mb: 4 }}>
            <AlertTitle>{t("Contact request rejected")}</AlertTitle>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t(
                "The facility rejected your contact request with a message. Please review the message and follow any guidance before resubmitting."
              )}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(`/contacts/${connection.id}/resubmit`)}
            >
              {t("Review")}
            </Button>
          </Alert>
        )}
      {!!connection?.activeRestrictions.length && (
        <ContactRestrictionsAlert
          contact={connection.contact}
          restrictions={connection.activeRestrictions}
        />
      )}
      <ContactDetails
        user={user}
        contact={connection.contact}
        connection={connection as unknown as Connection}
        facility={facility as Facility}
        permittedFeatures={permittedFeatures}
        statusComponent={
          !newResubmitUx &&
          connection.status === ConnectionStatus.Rejected &&
          mayRequestConnections ? (
            <Box
              sx={{
                flex: 1,
              }}
            >
              <ContactRejectedAlert
                statusDetails={connection.statusDetails}
                submitting={createPendingLoading}
                onSubmit={() => createPendingConnection()}
              />
            </Box>
          ) : (
            <Box>
              <ConnectionStatusChip status={connection.status} />
            </Box>
          )
        }
      />
      {user.__typename === "Visitor" &&
        connection.status !== ConnectionStatus.Inactive && (
          <Box sx={{ mt: 4, display: "flex" }}>
            <ArchiveConnectionButton
              firstName={connection.inmate.firstName}
              status={connection.status}
              connectionId={connection.id}
            />
            {canEditRelationship && (
              <EditConnectionRelationshipButton
                relationship={connection.relationship}
                connectionId={connection.id}
              />
            )}
          </Box>
        )}
    </Screen>
  );
}
